import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Popover,
    Select,
    Switch,
    Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../Firebase";
import { kProjectsCollectionName } from "../../utils/CollectionConstants";
import { TRANSCRIPTION_LANGUAGE_OPTIONS } from "./transcriptionLanguages";
import {
    AUDIO_EXPORT_FORMAT_OPTIONS,
    normalizeAudioExportFormat,
    normalizeAudioExportSampleRate,
} from "./exportAudioOptions";

type ProjectSettingsModalProps = {
    open: boolean;
    onClose: () => void;
    projectId: string | null | undefined;
    defaultTranscriptionLanguage: string | null | undefined;
    defaultAudioExportFormat: "m4a" | "wav";
    defaultAudioExportSampleRate: 44100 | 48000;
    showWordlessAudioEventsOnScript: boolean;
    applySmartTransitionsToNewScenes: boolean;
    onDefaultTranscriptionLanguageChange?: (languageCode: string) => void;
    onDefaultAudioExportFormatChange?: (value: "m4a" | "wav") => void;
    onDefaultAudioExportSampleRateChange?: (value: 44100 | 48000) => void;
    onShowWordlessAudioEventsOnScriptChange?: (value: boolean) => void;
    onApplySmartTransitionsToNewScenesChange?: (value: boolean) => void;
};

const sidebarHeaderStyle = {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    marginTop: "4px",
};

const sidebarItemStyle = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: "8px",
    cursor: "default",
    backgroundColor: active ? "var(--app-bg-subtle)" : "transparent",
    color: active ? "var(--app-text-primary)" : "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    border: active ? "1px solid var(--app-border-subtle)" : "1px solid transparent",
});

const settingLabelStyle = {
    fontFamily: '"Inter", sans-serif',
    fontSize: "14px",
    fontWeight: 450,
    color: "var(--app-text-primary)",
    // letterSpacing: "-0.01em",
};

const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
    open,
    onClose,
    projectId,
    defaultTranscriptionLanguage,
    defaultAudioExportFormat,
    defaultAudioExportSampleRate,
    showWordlessAudioEventsOnScript,
    applySmartTransitionsToNewScenes,
    onDefaultTranscriptionLanguageChange,
    onDefaultAudioExportFormatChange,
    onDefaultAudioExportSampleRateChange,
    onShowWordlessAudioEventsOnScriptChange,
    onApplySmartTransitionsToNewScenesChange,
}) => {
    const [isSavingLanguage, setIsSavingLanguage] = useState(false);
    const [isSavingAudioFormat, setIsSavingAudioFormat] = useState(false);
    const [isSavingAudioSampleRate, setIsSavingAudioSampleRate] = useState(false);
    const [isSavingWordless, setIsSavingWordless] = useState(false);
    const [isSavingSmartTransitions, setIsSavingSmartTransitions] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
    const [activeInfoKey, setActiveInfoKey] = useState<"language" | "audioFormat" | "audioSampleRate" | "wordless" | "smartTransitions" | null>(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
    }, [open]);

    const resolvedLanguage = useMemo(() => {
        const language = String(defaultTranscriptionLanguage ?? "").trim();
        return language || "auto";
    }, [defaultTranscriptionLanguage]);

    const persistProjectSettingsPatch = async (patch: Record<string, any>) => {
        const pid = String(projectId ?? "").trim();
        if (!pid) throw new Error("Project is not available.");
        const projectRef = doc(db, kProjectsCollectionName, pid);
        await updateDoc(projectRef, patch);
    };

    const handleLanguageChange = async (event: SelectChangeEvent<string>) => {
        const nextLanguage = String(event.target.value ?? "auto").trim() || "auto";
        const prevLanguage = resolvedLanguage;
        if (nextLanguage === prevLanguage) return;

        setError(null);
        setIsSavingLanguage(true);
        onDefaultTranscriptionLanguageChange?.(nextLanguage);
        try {
            await persistProjectSettingsPatch({ defaultTranscriptionLanguage: nextLanguage });
        } catch (e) {
            onDefaultTranscriptionLanguageChange?.(prevLanguage);
            setError("Could not save default transcription language.");
        } finally {
            setIsSavingLanguage(false);
        }
    };

    const handleWordlessToggle = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const prev = Boolean(showWordlessAudioEventsOnScript);
        if (checked === prev) return;

        setError(null);
        setIsSavingWordless(true);
        onShowWordlessAudioEventsOnScriptChange?.(checked);
        try {
            await persistProjectSettingsPatch({ showWordlessAudioEventsOnScript: checked });
        } catch (e) {
            onShowWordlessAudioEventsOnScriptChange?.(prev);
            setError("Could not save script event visibility setting.");
        } finally {
            setIsSavingWordless(false);
        }
    };

    const handleAudioFormatChange = async (next: "m4a" | "wav") => {
        const prev = normalizeAudioExportFormat(defaultAudioExportFormat);
        if (next === prev) return;

        setError(null);
        setIsSavingAudioFormat(true);
        onDefaultAudioExportFormatChange?.(next);
        try {
            await persistProjectSettingsPatch({ defaultAudioExportFormat: next });
        } catch (e) {
            onDefaultAudioExportFormatChange?.(prev);
            setError("Could not save default audio export format.");
        } finally {
            setIsSavingAudioFormat(false);
        }
    };

    const handleAudioSampleRateChange = async (next: 44100 | 48000) => {
        const prev = normalizeAudioExportSampleRate(defaultAudioExportSampleRate);
        if (next === prev) return;

        setError(null);
        setIsSavingAudioSampleRate(true);
        onDefaultAudioExportSampleRateChange?.(next);
        try {
            await persistProjectSettingsPatch({ defaultAudioExportSampleRate: next });
        } catch (e) {
            onDefaultAudioExportSampleRateChange?.(prev);
            setError("Could not save default audio export sample rate.");
        } finally {
            setIsSavingAudioSampleRate(false);
        }
    };

    const handleSmartTransitionsToggle = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const prev = Boolean(applySmartTransitionsToNewScenes);
        if (checked === prev) return;

        setError(null);
        setIsSavingSmartTransitions(true);
        onApplySmartTransitionsToNewScenesChange?.(checked);
        try {
            await persistProjectSettingsPatch({ applySmartTransitionsToNewScenes: checked });
        } catch (e) {
            onApplySmartTransitionsToNewScenesChange?.(prev);
            setError("Could not save smart transitions setting.");
        } finally {
            setIsSavingSmartTransitions(false);
        }
    };

    const openInfoPopover = (
        event: React.MouseEvent<HTMLElement>,
        key: "language" | "audioFormat" | "audioSampleRate" | "wordless" | "smartTransitions",
    ) => {
        setInfoAnchorEl(event.currentTarget);
        setActiveInfoKey(key);
    };

    const closeInfoPopover = () => {
        setInfoAnchorEl(null);
        setActiveInfoKey(null);
    };

    const infoPopoverText =
        activeInfoKey === "language"
            ? "Applies to new track uploads in this project."
            : activeInfoKey === "audioFormat"
                ? "Used as the default for audio exports and Separate speakers audio tracks."
                : activeInfoKey === "audioSampleRate"
                    ? "Used as the default sample rate for audio exports and Separate speakers audio tracks."
                : activeInfoKey === "wordless"
                    ? "Show transcript tokens such as laughs and other non-word audio events."
                : activeInfoKey === "smartTransitions"
                    ? "Automatically add Smart transitions when creating new scene boundaries."
                : "";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: "18px",
                    width: "860px",
                    maxWidth: "calc(100vw - 40px)",
                    height: "64vh",
                    maxHeight: "620px",
                    backgroundColor: "var(--app-bg-canvas)",
                },
            }}
        >
            <DialogContent sx={{ padding: 0, height: "100%", position: "relative" }}>
                <Box sx={{ display: "flex", height: "100%" }}>
                    <Box
                        sx={{
                            width: "240px",
                            padding: "24px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        <Typography sx={sidebarHeaderStyle}>Project</Typography>
                        <Box sx={sidebarItemStyle(true)}>
                            General
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, padding: "20px 20px 20px 0" }}>
                        <Box
                            sx={{
                                height: "100%",
                                backgroundColor: "var(--app-bg-surface)",
                                borderRadius: "14px",
                                border: "1px solid var(--app-border-subtle)",
                                padding: "24px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "20px",
                                overflowY: "auto",
                            }}
                        >
                            <Typography sx={{ fontFamily: "Inter", fontSize: "22px", fontWeight: 600, color: "var(--app-text-primary)" }}>
                                Settings
                            </Typography>

                            {error ? (
                                <Alert severity="error" sx={{ borderRadius: "10px" }}>
                                    {error}
                                </Alert>
                            ) : null}

                            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mt: 0.25 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Default transcription language
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "language")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Default transcription language info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingLanguage ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Select
                                            size="small"
                                            value={resolvedLanguage}
                                            onChange={handleLanguageChange}
                                            sx={{
                                                width: "170px",
                                                fontFamily: "Inter",
                                                fontSize: "13px",
                                                backgroundColor: "var(--app-bg-surface)",
                                                "& .MuiSelect-select": { py: 0.75, pr: 3.25 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 320,
                                                        "& .MuiMenuItem-root": {
                                                            fontFamily: "Inter",
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {TRANSCRIPTION_LANGUAGE_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Show wordless audio events on script
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "wordless")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Show wordless audio events info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingWordless ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Switch
                                            checked={Boolean(showWordlessAudioEventsOnScript)}
                                            onChange={handleWordlessToggle}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Apply smart transitions to new scenes
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "smartTransitions")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Apply smart transitions info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingSmartTransitions ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Switch
                                            checked={Boolean(applySmartTransitionsToNewScenes)}
                                            onChange={handleSmartTransitionsToggle}
                                        />
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Default audio export format
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "audioFormat")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Default audio export format info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingAudioFormat ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Select
                                            size="small"
                                            value={normalizeAudioExportFormat(defaultAudioExportFormat)}
                                            onChange={(e) => handleAudioFormatChange(normalizeAudioExportFormat(e.target.value))}
                                            sx={{
                                                width: "230px",
                                                fontFamily: "Inter",
                                                fontSize: "13px",
                                                backgroundColor: "var(--app-bg-surface)",
                                                "& .MuiSelect-select": { py: 0.75, pr: 3.25 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 320,
                                                        "& .MuiMenuItem-root": {
                                                            fontFamily: "Inter",
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {AUDIO_EXPORT_FORMAT_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Default audio export sample rate
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "audioSampleRate")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Default audio export sample rate info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingAudioSampleRate ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Box
                                            sx={{
                                                minWidth: "170px",
                                                display: "flex",
                                                alignItems: "center",
                                                backgroundColor: "var(--app-bg-subtle)",
                                                border: "1px solid var(--app-border-subtle)",
                                                borderRadius: "10px",
                                                p: "2px",
                                                gap: "2px",
                                                height: 34,
                                            }}
                                        >
                                            <Box
                                                onClick={() => handleAudioSampleRateChange(44100)}
                                                sx={{
                                                    display: "flex",
                                                    flex: 1,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: 28,
                                                    borderRadius: "8px",
                                                    fontFamily: "Inter",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                    color: normalizeAudioExportSampleRate(defaultAudioExportSampleRate) === 44100 ? "var(--app-text-primary)" : "var(--app-text-muted)",
                                                    backgroundColor: normalizeAudioExportSampleRate(defaultAudioExportSampleRate) === 44100 ? "var(--app-bg-surface)" : "transparent",
                                                }}
                                            >
                                                44100
                                            </Box>
                                            <Box
                                                onClick={() => handleAudioSampleRateChange(48000)}
                                                sx={{
                                                    display: "flex",
                                                    flex: 1,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: 28,
                                                    borderRadius: "8px",
                                                    fontFamily: "Inter",
                                                    fontSize: "13px",
                                                    fontWeight: 500,
                                                    cursor: "pointer",
                                                    color: normalizeAudioExportSampleRate(defaultAudioExportSampleRate) === 48000 ? "var(--app-text-primary)" : "var(--app-text-muted)",
                                                    backgroundColor: normalizeAudioExportSampleRate(defaultAudioExportSampleRate) === 48000 ? "var(--app-bg-surface)" : "transparent",
                                                }}
                                            >
                                                48000
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Popover
                    open={Boolean(infoAnchorEl && activeInfoKey)}
                    anchorEl={infoAnchorEl}
                    onClose={closeInfoPopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            borderRadius: "10px",
                            border: "1px solid var(--app-border-subtle)",
                            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.12)",
                            px: 1.25,
                            py: 1,
                            maxWidth: "280px",
                        },
                    }}
                >
                    <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)", lineHeight: 1.45 }}>
                        {infoPopoverText}
                    </Typography>
                </Popover>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectSettingsModal;
